import { useEffect, useMemo, useState } from 'react';
import { Checkbox, Divider, Space, Typography } from 'antd';
import { FileTextOutlined, MoneyCollectOutlined } from '@ant-design/icons';
import { useFormikContext } from 'formik';
import { Customer, DutyPayer } from 'store/customers/customer';
import useSession from 'hooks/useSession';
import { useRequestPromise } from 'hooks/useRequest';
import { getCustomer } from 'store/customers/client';
import DeclarationInput from 'components/ui/composed/declarations/formInput';
import DeclarationCheckbox from 'components/ui/composed/declarations/formCheckbox/DeclarationCheckbox';
import HeaderWithIcon from 'components/new-ui/composed/header/HeaderWithIcon';
import ThirdPartyBilling from './ThirdPartyBilling';
import VatTreatmentSelect from './VatTreatmentSelect';
import AEP5AuthorisationSection from './AEP5AuthorisationSection';
import DutyPayerSelect, { DutyPayerOption, getDutyPayerOptions } from './DutyPayerSelect';

interface Props {
    active: boolean;
    customer?: Customer;
}

const DutyVatBillingStep = ({ active, customer }: Props) => {
    const { customerId: tenantId } = useSession();
    const [checkedBillingDetails, setCheckedBillingDetails] = useState(false);

    const { getFieldProps, getFieldMeta, getFieldHelpers, values, setFieldValue } =
        useFormikContext<Partial<Customer>>();

    const { data: tenant } = useRequestPromise(() => (tenantId ? getCustomer(tenantId) : null), undefined, [tenantId]);

    const dutyPayerIsTheConsignee = values?.customerDetails?.dutyPayer === DutyPayer.CONSIGNEE;

    const dutyPayerOptions: DutyPayerOption[] = useMemo(
        () => getDutyPayerOptions(tenant?.eori, values.eori),
        [tenant?.eori, values.eori]
    );

    // Keep billing recipient name/email in sync with the contact details while the checkbox is checked
    useEffect(() => {
        if (!checkedBillingDetails) return;
        setFieldValue('customerDetails.billingName', values.contact?.name);
        setFieldValue('customerDetails.billingEmail', values.contact?.email);
    }, [checkedBillingDetails, values.contact?.name, values.contact?.email, setFieldValue]);

    return (
        <Space direction="vertical" size={16} style={{ width: '100%', display: active ? 'flex' : 'none' }}>
            <HeaderWithIcon
                title="Duty & Vat"
                subtitle={
                    <Typography.Text>
                        Set Duty & VAT defaults for this Party, these can be overridden at any time per declaration
                    </Typography.Text>
                }
                iconProps={{ icon: <MoneyCollectOutlined />, type: 'info' }}
                action={<div></div>}
                style={{ marginTop: 16 }}
            />

            <DutyPayerSelect
                required
                condensed
                fieldProps={getFieldProps('customerDetails.dutyPayer')}
                fieldMeta={getFieldMeta('customerDetails.dutyPayer')}
                fieldHelper={getFieldHelpers('customerDetails.dutyPayer')}
                options={dutyPayerOptions}
                label="Duty Payer"
            />

            {dutyPayerIsTheConsignee && (
                <AEP5AuthorisationSection
                    checked={values?.customerDetails?.confirmedTanUsage === true}
                    onChange={(e) => {
                        setFieldValue('customerDetails.confirmedTanUsage', e.target.checked);
                    }}
                />
            )}

            <VatTreatmentSelect
                required
                condensed
                fieldProps={getFieldProps('customerDetails.vatTreatment')}
                fieldMeta={getFieldMeta('customerDetails.vatTreatment')}
                fieldHelper={getFieldHelpers('customerDetails.vatTreatment')}
                label="VAT Treatment"
            />

            <DeclarationCheckbox
                label="VAT Exemption"
                fieldProps={getFieldProps('customerDetails.vatExemption')}
                fieldMeta={getFieldMeta('customerDetails.vatExemption')}
                fieldHelper={getFieldHelpers('customerDetails.vatExemption')}
            >
                <Typography.Text>Section 56 held - VAT not charged at import</Typography.Text>
            </DeclarationCheckbox>

            <Divider style={{ padding: 0, margin: 0 }} />

            <HeaderWithIcon
                title="Billing Details"
                subtitle={
                    <Typography.Text>
                        Default invoice recipient for this party - can be updated at any time
                    </Typography.Text>
                }
                iconProps={{ icon: <FileTextOutlined />, type: 'accent' }}
                action={<div></div>}
                style={{ marginTop: 16 }}
            />

            <Checkbox checked={checkedBillingDetails} onChange={(e) => setCheckedBillingDetails(e.target.checked)}>
                Same as contact details
            </Checkbox>

            <DeclarationInput
                condensed
                labelSpacing="medium"
                label="Billing Recipient Name"
                fieldMeta={getFieldMeta('customerDetails.billingName')}
                fieldProps={getFieldProps('customerDetails.billingName')}
                fieldHelper={getFieldHelpers('customerDetails.billingName')}
                disabled={checkedBillingDetails}
            />

            <DeclarationInput
                condensed
                labelSpacing="medium"
                label="Billing Email"
                fieldMeta={getFieldMeta('customerDetails.billingEmail')}
                fieldProps={getFieldProps('customerDetails.billingEmail')}
                fieldHelper={getFieldHelpers('customerDetails.billingEmail')}
                disabled={checkedBillingDetails}
            />

            <DeclarationInput
                condensed
                labelSpacing="medium"
                label="Billing Customer Code"
                fieldMeta={getFieldMeta('customerDetails.billingCustomerCode')}
                fieldProps={getFieldProps('customerDetails.billingCustomerCode')}
                fieldHelper={getFieldHelpers('customerDetails.billingCustomerCode')}
            />

            <ThirdPartyBilling
                editingPartyId={customer?.id}
                thirdPartyId={values.customerDetails?.thirdParty}
                onChange={(id) => {
                    setFieldValue('customerDetails.thirdParty', id);
                }}
                onClear={() => {
                    setFieldValue('customerDetails.thirdParty', undefined);
                }}
            />
        </Space>
    );
};

export default DutyVatBillingStep;
